import { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getUpiList,
  addUpi,
  updateUpi,
  deleteUpi,
} from "../../services/upiService";

/* ─── helpers ─── */
const toastCfg = { autoClose: 1200 };
const initForm = { upi_id: "", name: "", status: "active" };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Add / Edit Modal ─── */
const UpiModal = ({ show, onHide, editItem, onSaved }) => {
  const isEdit = Boolean(editItem?.id);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setErrors({});
      setForm(isEdit ? { upi_id: editItem.upi_id, name: editItem.name, status: editItem.status } : initForm);
    }
  }, [show, editItem]);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.upi_id.trim()) err.upi_id = "UPI ID is required";
    if (!form.name.trim())   err.name   = "Name is required";
    setErrors(err);
    return !Object.keys(err).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await updateUpi(editItem.id, form);
        toast.success("Updated successfully", toastCfg);
      } else {
        await addUpi(form);
        toast.success("Added successfully", toastCfg);
      }
      onSaved();
      onHide();
    } catch {
      toast.error("Something went wrong", toastCfg);
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>{isEdit ? "Edit UPI" : "Add New UPI"}</div>
          <div style={ms.headerSub}>
            {isEdit ? `Editing: ${editItem?.upi_id}` : "Fill in the details to add a UPI"}
          </div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={ms.body}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={ms.label}>UPI ID <span style={{ color: "#ef4444" }}>*</span></label>
              <input style={ms.input} placeholder="e.g. name@upi" value={form.upi_id} onChange={set("upi_id")} />
              {errors.upi_id && <div style={ms.fieldErr}>{errors.upi_id}</div>}
            </div>
            <div>
              <label style={ms.label}>Name <span style={{ color: "#ef4444" }}>*</span></label>
              <input style={ms.input} placeholder="Display name" value={form.name} onChange={set("name")} />
              {errors.name && <div style={ms.fieldErr}>{errors.name}</div>}
            </div>
            <div>
              <label style={ms.label}>Status</label>
              <select style={ms.input} value={form.status} onChange={set("status")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div style={ms.footer}>
          <button type="button" style={ms.cancelBtn} onClick={onHide}>Cancel</button>
          <button type="submit" style={ms.saveBtn} disabled={saving}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
              : isEdit ? "Update UPI" : "Add UPI"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ─── Modal styles ─── */
const ms = {
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9",
  },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub:   { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: {
    background: "#f1f5f9", border: "none", borderRadius: 8,
    width: 32, height: 32, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b",
  },
  body:  { padding: "18px 24px" },
  label: {
    display: "block", fontSize: "0.75rem", fontWeight: 700,
    color: "#475569", marginBottom: 5,
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    width: "100%", padding: "9px 12px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  fieldErr: { fontSize: "0.72rem", color: "#ef4444", marginTop: 4 },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9",
  },
  cancelBtn: {
    padding: "8px 18px", background: "#f1f5f9", border: "none",
    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
    fontWeight: 600, color: "#475569", cursor: "pointer",
  },
  saveBtn: {
    padding: "8px 24px",
    background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 8, fontFamily: "inherit",
    fontSize: "0.85rem", fontWeight: 700, color: "#fff",
    cursor: "pointer", display: "flex", alignItems: "center",
  },
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export const UpiMangeLayer = () => {
  const [upiList, setUpiList] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  /* ── fetch ── */
  const fetchUpi = useCallback(async () => {
    try {
      const res = await getUpiList();
      if (res?.status) setUpiList(res.data || []);
    } catch {
      toast.error("Failed to load data", toastCfg);
    }
  }, []);

  useEffect(() => { fetchUpi(); }, [fetchUpi]);

  /* ── delete ── */
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this UPI?")) return;
    try {
      await deleteUpi(id);
      toast.success("Deleted", toastCfg);
      fetchUpi();
    } catch {
      toast.error("Delete failed", toastCfg);
    }
  }, [fetchUpi]);

  /* ── open modal ── */
  const openAdd  = () => { setEditItem(null);  setShowModal(true); };
  const openEdit = (item) => { setEditItem(item); setShowModal(true); };

  /* ── stats ── */
  const stats = useMemo(() => ({
    total:    upiList.length,
    active:   upiList.filter((u) => u.status === "active").length,
    inactive: upiList.filter((u) => u.status === "inactive").length,
  }), [upiList]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) =>
        row.index + 1 + pagination.pageIndex * pagination.pageSize,
    },
    {
      accessorKey: "upi_id",
      header: "UPI ID",
      cell: ({ getValue }) => <span style={ts.mono}>{getValue()}</span>,
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => <span style={ts.name}>{getValue()}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const active = getValue() === "active";
        return (
          <span style={{
            ...ts.badge,
            background: active ? "#dcfce7" : "#fee2e2",
            color:      active ? "#16a34a" : "#dc2626",
          }}>
            {active ? "● Active" : "○ Inactive"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button style={ts.editBtn} onClick={() => openEdit(row.original)}>
            <Icon icon="solar:pen-bold" style={{ fontSize: 13 }} />
            Edit
          </button>
          <button style={ts.delBtn} onClick={() => handleDelete(row.original.id)}>
            <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: 13 }} />
          </button>
        </div>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize, handleDelete]);

  /* ── table instance ── */
  const table = useReactTable({
    data: upiList,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.upi_id?.toLowerCase().includes(v) ||
        row.original.name?.toLowerCase().includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div style={s.root}>
      {/* ── Stats row ── */}
      <div style={s.statsRow}>
        {[
          { label: "Total UPIs", value: stats.total,    icon: "solar:card-bold-duotone",          color: "#4B5EAA" },
          { label: "Active",     value: stats.active,   icon: "solar:check-circle-bold-duotone",  color: "#16a34a" },
          { label: "Inactive",   value: stats.inactive, icon: "solar:close-circle-bold-duotone",  color: "#dc2626" },
        ].map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: `${c.color}14`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 20 }} />
            </div>
            <div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Card ── */}
      <div style={s.card}>
        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search UPI ID or name…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>

          <div style={s.toolbarRight}>
            <button style={s.addBtn} onClick={openAdd}>
              <Icon icon="solar:add-circle-bold" style={{ fontSize: 15 }} />
              Add UPI
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:card-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No UPI entries found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, upiList.length)}</strong>
            {" "}of <strong>{upiList.length}</strong> UPIs
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}             disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}               disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const pg = Math.max(0, Math.min(pageIndex - 3, pageCount - 7)) + i;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                   disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)}  disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <UpiModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editItem={editItem}
        onSaved={fetchUpi}
      />
    </div>
  );
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 },
  statCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  statIcon:  { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },

  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },

  searchWrap:  { position: "relative", display: "flex", alignItems: "center" },
  searchIcon:  { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 240, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },
  addBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "7px 16px",
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", borderRadius: 9,
    fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", whiteSpace: "nowrap",
  },

  tableWrap: { overflowX: "auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap", textAlign: "left",
  },
  td: {
    padding: "9px 14px", borderBottom: "1px solid #f8fafc",
    color: "#334155", verticalAlign: "middle",
  },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },

  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo:      { fontSize: "0.78rem", color: "#64748b" },
  pgBtns:      { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  name:  { fontWeight: 600, color: "#1e293b", fontSize: "0.83rem" },
  mono:  { fontFamily: "monospace", fontSize: "0.81rem", color: "#334155" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap",
  },
  editBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 10px",
    background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
    fontSize: "0.75rem", fontWeight: 600,
  },
  delBtn: {
    display: "inline-flex", alignItems: "center",
    padding: "4px 8px",
    background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
  },
};
